import {
  Comments,
  LibraryPrompt,
  SelectedUserPrompt,
  UserPromptVersion,
  UserSavedPrompt,
} from '@Models/types';
import React, { createContext, useContext, useState, useCallback } from 'react';
import { useUtilityContext } from './UtilityContext';
import { promptToolboxAPI } from '@Lib/axios/prompt-toolbox';
import { useAuth } from '../CognitoAuthProvider';
import showToast from '@Common/toast/showToast';

interface PromptToolboxContextProps {
  // State
  userPrompts: UserSavedPrompt[];
  libraryPrompts: LibraryPrompt[];
  selectedLibrary: LibraryPrompt[];
  selectedPromptVersion: SelectedUserPrompt | null;

  // Methods
  fetchUserPrompts: () => Promise<void>;
  fetchLibraryPrompts: () => Promise<void>;
  fetchLibraryPrompt: (libraryId: string) => Promise<LibraryPrompt>;
  setSelectedLibrary: React.Dispatch<React.SetStateAction<LibraryPrompt[]>>;
  setSelectedPromptVersion: React.Dispatch<
    React.SetStateAction<SelectedUserPrompt | null>
  >;
  sharePrompt: () => Promise<void>;
  importLibrary: () => Promise<void>;
  removeLibraryPrompt: (promptId: string) => Promise<void>;
  postComment: (promptId: string, comment: Comments) => Promise<void>;
  deleteComment: (promptId: string, commentIndex: number) => Promise<void>;
  postVote: (promptId: string, voteType: string) => Promise<void>;
  addNewPrompt: (input: Omit<UserSavedPrompt, 'id'>) => Promise<void>;
  addNewVersion: (input: UserPromptVersion) => Promise<void>;
  deletePrompt: () => Promise<void>;
  deleteVersion: () => Promise<void>;
  renamePrompt: (newName: string) => Promise<void>;
}

const PromptToolboxContext = createContext<
  PromptToolboxContextProps | undefined
>(undefined);

export const PromptToolboxContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { setLoader, setView } = useUtilityContext();
  const { logout } = useAuth();

  // Initialize API with logout function
  const promptToolbox = promptToolboxAPI(logout);

  // State management
  const [userPrompts, setUserPrompts] = useState<UserSavedPrompt[]>([]);
  const [libraryPrompts, setLibraryPrompts] = useState<LibraryPrompt[]>([]);
  const [selectedLibrary, setSelectedLibrary] = useState<LibraryPrompt[]>([]);
  const [selectedPromptVersion, setSelectedPromptVersion] =
    useState<SelectedUserPrompt | null>(null);

  // Fetch user's saved prompts
  const fetchUserPrompts = useCallback(async () => {
    try {
      setLoader('userPrompts', true);
      const prompts = await promptToolbox.getUserPrompts();
      if (prompts.length > 0) {
        setUserPrompts(prompts);
      } else {
        setUserPrompts([]);
      }
    } catch (error) {
      console.error('Error fetching user prompts:', error);
    } finally {
      setLoader('userPrompts', false);
    }
  }, [promptToolbox, setLoader]);

  // Fetch library prompts
  const fetchLibraryPrompts = useCallback(async () => {
    setLoader('libraryPrompts', true);
    try {
      const library = await promptToolbox.getLibraryPrompts();
      if (library.length > 0) {
        setLibraryPrompts(library);
      } else {
        setLibraryPrompts([]);
      }
    } catch (error) {
      console.error('Error fetching library prompts:', error);
    } finally {
      setLoader('libraryPrompts', false);
    }
  }, [promptToolbox, setLoader]);

  // Fetch a specific library prompt by ID
  const fetchLibraryPrompt = useCallback(
    async (libraryId: string) => {
      try {
        return await promptToolbox.getLibraryPrompt(libraryId);
      } catch (error) {
        console.error('Error fetching library prompt:', error);
        throw error;
      }
    },
    [promptToolbox]
  );

  // Share a prompt to the library
  const sharePrompt = useCallback(async () => {
    if (selectedPromptVersion) {
      setLoader('sharePrompt', true);
      const libraryEntry: LibraryPrompt = {
        id: selectedPromptVersion.id,
        author: selectedPromptVersion.version.updatedBy
          ? selectedPromptVersion.version.updatedBy
          : selectedPromptVersion.author,
        name: selectedPromptVersion.name,
        version: selectedPromptVersion.version,
      };
      try {
        const libraryPrompt =
          await promptToolbox.createLibraryPrompt(libraryEntry);
        setLibraryPrompts((prev) => [libraryPrompt, ...prev]);
        setView('Library');
      } catch (error: unknown) {
        if (error instanceof Error) {
          const e = error as { status?: number };

          if (e.status === 409) {
            showToast.error(
              'Duplicate prompt detected! Please change the name or content.'
            );
          } else if (e.status === 400) {
            showToast.error('Missing required fields!');
          } else {
            showToast.error('Something went wrong!');
          }
        }
      } finally {
        setLoader('sharePrompt', false);
      }
    }
  }, [promptToolbox, selectedPromptVersion, setLoader, setView]);

  // Import prompts from library to user's collection
  const importLibrary = useCallback(async () => {
    if (selectedLibrary.length > 0) {
      setLoader('importPrompt', true);
      try {
        const response =
          await promptToolbox.importLibraryPrompt(selectedLibrary);
        const prompts = response.inserted;
        if (prompts) {
          setSelectedPromptVersion(null);
          setUserPrompts((prev) => [...prompts, ...prev]);
          const select: SelectedUserPrompt = {
            ...prompts[0],
            version: prompts[0].version[0],
            versions: [prompts[0].version[0].version],
          };
          setSelectedPromptVersion(select);
          setView('Diff');
        }
        console.log(response.message);
        setSelectedLibrary([]);
      } catch (error) {
        console.error('Error importing library prompts:', error);
      } finally {
        setLoader('importPrompt', false);
      }
    }
  }, [promptToolbox, selectedLibrary, setLoader, setView]);

  // Remove a prompt from the library
  const removeLibraryPrompt = useCallback(
    async (promptId: string) => {
      setLoader('deleteLibrary', true);
      try {
        const response = await promptToolbox.deleteLibraryPrompt(promptId);
        console.log(response.message);
        setLibraryPrompts(libraryPrompts.filter((i) => i._id !== promptId));
      } catch (error) {
        console.error('Error removing library prompt:', error);
      } finally {
        setLoader('deleteLibrary', false);
      }
    },
    [promptToolbox, libraryPrompts, setLoader]
  );

  // Add a comment to a library prompt
  const postComment = useCallback(
    async (promptId: string, comment: Comments) => {
      setLoader('commentLibrary', true);
      try {
        await promptToolbox.commentLibraryPrompt(promptId, comment);
        // Update the local state with the new comment
        setLibraryPrompts(
          libraryPrompts.map((prompt) =>
            prompt.id === promptId
              ? {
                  ...prompt,
                  comments: [...(prompt.comments || []), comment],
                }
              : prompt
          )
        );
      } catch (error) {
        console.error('Failed to post comment. Details:', error);
      } finally {
        setLoader('commentLibrary', false);
      }
    },
    [promptToolbox, libraryPrompts, setLoader]
  );
  // Delete a Library Prompt Comment
  const deleteComment = useCallback(
    async (promptId: string, commentIndex: number) => {
      setLoader('deleteComment', true);
      try {
        const response = await promptToolbox.deleteLibraryPromptComment(
          promptId,
          commentIndex
        );
        console.log(response.message);
      } catch (error) {
        console.error('Error deleting a comment: ', error);
      } finally {
        setLoader('deleteComment', false);
      }
    },
    [promptToolbox, libraryPrompts, setLoader]
  );
  // Vote on a library prompt
  const postVote = useCallback(
    async (promptId: string, voteType: string) => {
      setLoader('voteLibrary', true);
      try {
        const response = await promptToolbox.voteLibraryPrompt(
          promptId,
          voteType
        );
        console.log(response.message);
      } catch (error) {
        console.error('Error voting on library prompt:', error);
      } finally {
        setLoader('voteLibrary', false);
      }
    },
    [promptToolbox, setLoader]
  );

  // Add a new user prompt
  const addNewPrompt = useCallback(
    async (input: Omit<UserSavedPrompt, 'id'>) => {
      console.log('INPUT: ', input);
      setSelectedPromptVersion(null);
      setLoader('newPrompt', true);
      try {
        const newPrompt = await promptToolbox.createUserPrompt(input);
        console.log('NEWPROMPT: ', newPrompt);
        setUserPrompts((prev) => [newPrompt, ...prev]);
        const select: SelectedUserPrompt = {
          ...newPrompt,
          versions: newPrompt.version.map((i) => i.version),
          version: newPrompt.version[0],
        };
        setSelectedPromptVersion(select);
        setView('Diff');
      } catch (error) {
        console.error('Error adding new prompt:', error);
      } finally {
        setLoader('newPrompt', false);
      }
    },
    [promptToolbox, setLoader, setView]
  );

  // Add a new version to an existing prompt
  const addNewVersion = useCallback(
    async (input: UserPromptVersion) => {
      if (selectedPromptVersion) {
        setLoader('newVersion', true);
        try {
          const newVersion = await promptToolbox.addUserPromptVersion(
            selectedPromptVersion.id,
            input,
            input.version
          );

          // Update the selected prompt version
          const select: SelectedUserPrompt = {
            ...selectedPromptVersion,
            versions: [newVersion.version, ...selectedPromptVersion.versions],
            version: newVersion,
          };
          setSelectedPromptVersion(select);

          // Update the prompts list with the new version
          setUserPrompts(
            userPrompts.map((prompt) =>
              prompt.id === selectedPromptVersion.id
                ? {
                    ...prompt,
                    version: [newVersion, ...prompt.version],
                  }
                : prompt
            )
          );
        } catch (error) {
          console.error('Error adding new version:', error);
        } finally {
          setLoader('newVersion', false);
        }
      }
    },
    [promptToolbox, selectedPromptVersion, userPrompts, setLoader]
  );

  // Delete a prompt
  const deletePrompt = useCallback(async () => {
    if (selectedPromptVersion) {
      setLoader('deletePrompt', true);
      try {
        const response = await promptToolbox.deleteUserPrompt(
          selectedPromptVersion.id
        );
        console.log(response.message);
        setSelectedPromptVersion(null);
        setUserPrompts(
          userPrompts.filter((i) => i.id !== selectedPromptVersion.id)
        );
      } catch (error) {
        console.error('Error deleting prompt:', error);
      } finally {
        setLoader('deletePrompt', false);
      }
    }
  }, [promptToolbox, selectedPromptVersion, userPrompts, setLoader]);

  // Delete a version of a prompt
  const deleteVersion = useCallback(async () => {
    if (selectedPromptVersion) {
      setLoader('deleteVersion', true);
      try {
        const response = await promptToolbox.deleteUserPromptVersion(
          selectedPromptVersion.id,
          selectedPromptVersion.version.version
        );
        console.log(response.message);
        setSelectedPromptVersion(null);

        // Filter out the deleted version and remove prompts with no versions
        const updatedPrompts = userPrompts
          .map((prompt) =>
            prompt.id === selectedPromptVersion.id
              ? {
                  ...prompt,
                  version: prompt.version.filter(
                    (v) => v.version !== selectedPromptVersion.version.version
                  ),
                }
              : prompt
          )
          .filter((prompt) => prompt.version.length > 0);

        setUserPrompts(updatedPrompts);
      } catch (error) {
        console.error('Error deleting version:', error);
      } finally {
        setLoader('deleteVersion', false);
      }
    }
  }, [promptToolbox, selectedPromptVersion, userPrompts, setLoader]);

  // Rename a prompt
  const renamePrompt = useCallback(
    async (newName: string) => {
      if (selectedPromptVersion) {
        setLoader('renamePrompt', true);
        try {
          const response = await promptToolbox.renameUserPrompt(
            selectedPromptVersion.id,
            newName
          );
          console.log(response.message);

          // Update the selected prompt
          const updatedPrompt: SelectedUserPrompt = {
            ...selectedPromptVersion,
            name: newName,
          };
          setSelectedPromptVersion(updatedPrompt);

          // Update the prompts list
          setUserPrompts(
            userPrompts.map((prompt) =>
              prompt.id === updatedPrompt.id
                ? { ...prompt, name: newName }
                : prompt
            )
          );
        } catch (error) {
          console.error('Error renaming prompt:', error);
        } finally {
          setLoader('renamePrompt', false);
        }
      }
    },
    [promptToolbox, selectedPromptVersion, userPrompts, setLoader]
  );

  // Provide all values and methods through context
  const value = {
    userPrompts,
    libraryPrompts,
    selectedLibrary,
    selectedPromptVersion,
    fetchUserPrompts,
    fetchLibraryPrompts,
    fetchLibraryPrompt,
    setSelectedLibrary,
    setSelectedPromptVersion,
    sharePrompt,
    importLibrary,
    removeLibraryPrompt,
    postComment,
    deleteComment,
    postVote,
    addNewPrompt,
    addNewVersion,
    deletePrompt,
    deleteVersion,
    renamePrompt,
  };

  return (
    <PromptToolboxContext.Provider value={value}>
      {children}
    </PromptToolboxContext.Provider>
  );
};

export const usePromptToolboxContext = (): PromptToolboxContextProps => {
  const context = useContext(PromptToolboxContext);
  if (!context)
    throw new Error(
      'usePromptToolboxContext must be used within a PromptToolboxContextProvider'
    );
  return context;
};
